import React from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Drag an appointment to a new time to reschedule it (onReschedule asks first).
const DnDCalendar = withDragAndDrop(Calendar);

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Only upcoming sessions without notes can be moved.
const canMove = (appt) => !appt.has_consultation && !appt.is_cancelled && appt.attendance !== 'cancelled'
  && appt.confirmation_status !== 'Declined' && new Date(`${appt.date}T${(appt.time || '00:00').slice(0, 5)}`) > new Date();

const CalendarView = ({ appointments, onSlotSelect, onAppointmentSelect, onReschedule }) => {
  // Convert backend appointments to react-big-calendar events
  const events = appointments.map((appt) => {
    // appt.date is YYYY-MM-DD, appt.time is HH:MM AM/PM
    const [hoursStr, minutesStrPart] = appt.time.split(':');
    const [minutesStr, period] = minutesStrPart.split(' ');
    
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const startDate = new Date(appt.date);
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + (appt.duration_minutes || 30) * 60000);

    return {
      id: appt.id,
      title: `${appt.patient_name} with ${appt.doctor_name}`,
      start: startDate,
      end: endDate,
      resource: appt,
    };
  });

  const eventStyleGetter = (event, start, end, isSelected) => {
    // Custom styling for the event blocks
    const style = {
      backgroundColor: 'var(--color-primary-50)',
      borderRadius: '6px',
      opacity: 0.95,
      color: 'var(--color-primary-800)',
      border: '1px solid var(--color-primary-200)',
      display: 'block',
      padding: '2px 4px',
      fontSize: '0.75rem', // reduced font size
      fontWeight: '600',
      lineHeight: '1.2',
    };
    return {
      style,
    };
  };

  return (
    <div style={{ height: '700px' }} className="bg-white rounded-2xl shadow-sm border border-secondary-100 p-6 overflow-hidden calendar-container">
      <DnDCalendar
        localizer={localizer}
        events={events}
        draggableAccessor={(event) => Boolean(onReschedule) && canMove(event.resource)}
        resizable={false}
        onEventDrop={({ event, start }) => {
          if (!onReschedule) return;
          // Dropped on a day in Month view: keep the original time.
          const s = new Date(start);
          if (s.getHours() === 0 && s.getMinutes() === 0 && (event.start.getHours() || event.start.getMinutes())) {
            s.setHours(event.start.getHours(), event.start.getMinutes(), 0, 0);
          }
          if (s.getTime() !== event.start.getTime()) onReschedule(event.resource, s);
        }}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        selectable
        onSelectSlot={(slotInfo) => {
          // Check if slotInfo.action is 'click' or 'select'
          // slotInfo.start has the Date object
          onSlotSelect(slotInfo.start);
        }}
        onSelectEvent={(event) => {
          onAppointmentSelect(event.resource);
        }}
        eventPropGetter={eventStyleGetter}
        step={15}
        timeslots={4}
        scrollToTime={new Date(1970, 0, 1, 8, 0)}
        views={[Views.MONTH, Views.WEEK, Views.DAY]}
        defaultView={Views.MONTH}
        popup
        tooltipAccessor={(event) => event.title}
        components={{
            toolbar: CustomToolbar
        }}
      />
      <style>{`
        /* Overriding react-big-calendar defaults to match the theme */
        .calendar-container {
            font-size: 0.85rem;
        }
        .rbc-btn-group button {
            color: var(--color-secondary-600);
            border-color: var(--color-secondary-200);
            font-weight: 500;
            font-size: 0.85rem;
        }
        .rbc-btn-group button.rbc-active {
            background-color: var(--color-primary-600);
            color: white;
            border-color: var(--color-primary-600);
            box-shadow: none;
        }
        .rbc-btn-group button:hover:not(.rbc-active) {
            background-color: var(--color-secondary-50);
        }
        .rbc-header {
            padding: 8px 0;
            font-weight: 600;
            color: var(--color-secondary-700);
            border-bottom: 1px solid var(--color-secondary-200);
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        .rbc-month-view, .rbc-time-view {
            border-color: var(--color-secondary-200);
            border-radius: 8px;
            overflow: hidden;
        }
        .rbc-day-bg {
            border-left: 1px solid var(--color-secondary-200);
        }
        .rbc-month-row {
            border-top: 1px solid var(--color-secondary-200);
        }
        .rbc-today {
            background-color: var(--color-primary-50);
        }
        .rbc-time-content {
            border-top: 1px solid var(--color-secondary-200);
        }
        .rbc-timeslot-group {
            border-bottom: 1px solid var(--color-secondary-200);
        }
        .rbc-time-header.rbc-overflowing {
            border-right: 1px solid var(--color-secondary-200);
        }
        .rbc-time-slot {
            font-size: 0.75rem;
            color: var(--color-secondary-500);
        }
        .rbc-event-content {
            font-size: 0.75rem;
            line-height: 1.2;
        }
      `}</style>
    </div>
  );
};

// Optional: A custom toolbar to match the design closer, but default is fine if styled.
const CustomToolbar = (toolbar) => {
  const goToBack = () => toolbar.onNavigate('PREV');
  const goToNext = () => toolbar.onNavigate('NEXT');
  const goToCurrent = () => toolbar.onNavigate('TODAY');

  const label = () => {
    const date = toolbar.date;
    return <span>{toolbar.label}</span>;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div className="flex items-center gap-2">
        <button onClick={goToBack} className="p-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-secondary-600 transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
        </button>
        <button onClick={goToNext} className="p-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-secondary-600 transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
        </button>
        <button onClick={goToCurrent} className="px-4 py-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-secondary-700 font-medium transition-colors">
          Today
        </button>
        <span className="text-xl font-bold text-secondary-900 ml-4">{label()}</span>
      </div>
      <div className="flex bg-secondary-100 p-1 rounded-lg">
        {toolbar.views.map((view) => (
          <button
            key={view}
            onClick={() => toolbar.onView(view)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
              toolbar.view === view
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-200/50'
            }`}
          >
            {view}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarView;
